import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { GoogleButton } from "../components/GoogleButton";
import { FacebookButton } from "../components/FacebookButton";
import { Logo } from "../components/Logo";
import { track } from "../lib/analytics";
import { useSession } from "../state";

export function Login() {
  const { signIn } = useSession();
  const nav = useNavigate();
  const loc = useLocation();
  const next = (loc.state as any)?.next || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await signIn(email.trim(), password);
      track("signin", { method: "password" });
      nav(next, { replace: true });
    } catch (e: any) {
      setErr(humanize(e?.message || e?.code || "couldn't sign in"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <h1 className="font-display font-black text-3xl mb-1">welcome back</h1>
      <p className="text-ink-soft mb-6 text-sm">sign in to continue</p>

      <div className="space-y-2">
        <GoogleButton />
        <FacebookButton />
      </div>

      <Divider />

      <form onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="text-xs text-ink-soft">email</span>
          <input
            type="email"
            autoComplete="email"
            required
            className="input mt-1"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-xs text-ink-soft">password</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            className="input mt-1"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <button type="submit" disabled={busy} className="btn-primary w-full">
          {busy ? "signing in…" : "sign in"}
        </button>
        {err && <p className="text-sm text-coral-dark">{err}</p>}
      </form>

      <div className="mt-6 text-sm text-ink-soft flex justify-between">
        <Link to="/forgot" className="hover:text-ink underline">forgot password?</Link>
        <Link to="/signup" className="hover:text-ink underline">create account</Link>
      </div>
    </AuthShell>
  );
}

export function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col">
      <header className="px-6 md:px-10 py-5 max-w-5xl mx-auto w-full">
        <Link to="/"><Logo /></Link>
      </header>
      <main className="flex-1 grid place-items-center px-6 pb-12">
        <div className="bg-cream-dark/30 backdrop-blur rounded-3xl shadow-soft p-7 max-w-sm w-full animate-rise">
          {children}
        </div>
      </main>
    </div>
  );
}

export function Divider() {
  return (
    <div className="my-5 flex items-center gap-3 text-xs text-ink-mute">
      <span className="flex-1 h-px bg-ink/10" /> or <span className="flex-1 h-px bg-ink/10" />
    </div>
  );
}

function humanize(msg: string): string {
  const m = msg.toLowerCase();
  if (m.includes("not authorized") || m.includes("incorrect")) return "wrong email or password";
  if (m.includes("usernotconfirmed") || m.includes("not confirmed")) return "please confirm your email first — check your inbox";
  if (m.includes("usernotfound")) return "no account with that email";
  if (m.includes("invalidpassword") || m.includes("password did not conform")) return "password doesn't meet the requirements (10+ chars, mixed case, number)";
  if (m.includes("passwordresetrequired")) return "you need to reset your password";
  if (m.includes("limitexceeded") || m.includes("too many")) return "too many attempts — wait a minute and retry";
  return msg;
}
